export const SUB_AGENT_THREAD_SCHEMA_VERSION = "l9_sub_agent_thread.v1";

export const SUB_AGENT_SPAWN_TOOL_NAMES = [
  "spawn",
  "spawn_with_approval",
] as const;

export type SubAgentThreadMode = "async" | "ask";

export type SubAgentThreadState =
  | "queued"
  | "running"
  | "working"
  | "paused"
  | "completed"
  | "failed"
  | "cancelled"
  | "timed_out";

export type SubAgentViewUnavailableReason =
  | "not_persisted"
  | "parent_unavailable"
  | "session_conflict"
  | "reservation_failed"
  | "scheduler_failed";

export interface SubAgentThreadReference {
  taskId: string;
  childSessionId: string;
  parentSessionId: string;
  parentRunId: string | null;
  agentId: string;
  mode: SubAgentThreadMode;
  state: SubAgentThreadState;
  viewAvailable: boolean;
  reasonCode: SubAgentViewUnavailableReason | null;
  promptSummary: string | null;
}

/** Safe, non-navigable preview available while a spawn tool is in flight. */
export interface PendingSubAgentSpawn {
  agentId: string;
  promptSummary: string;
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const MODES = new Set<SubAgentThreadMode>(["async", "ask"]);
const STATES = new Set<SubAgentThreadState>([
  "queued",
  "running",
  "working",
  "paused",
  "completed",
  "failed",
  "cancelled",
  "timed_out",
]);
const REASONS = new Set<SubAgentViewUnavailableReason>([
  "not_persisted",
  "parent_unavailable",
  "session_conflict",
  "reservation_failed",
  "scheduler_failed",
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Detect a durable sub-agent session from fields returned by session detail.
 *
 * `session_type` is authoritative on current servers. The reserved metadata
 * marker keeps the UI safely read-only against transitional servers that
 * persisted the V1 reservation metadata before exposing the additive type
 * discriminator in their response schema. Missing fields deliberately fall
 * back to normal-session rendering for older deployments.
 */
export function isSubAgentSessionData(session: unknown): boolean {
  if (!isRecord(session)) return false;
  if (session.session_type !== undefined && session.session_type !== null) {
    return session.session_type === "sub_agent";
  }

  const metadata = session.metadata;
  if (!isRecord(metadata)) return false;
  const marker = metadata.l9_sub_agent_thread;
  return isRecord(marker)
    && marker.schema_version === SUB_AGENT_THREAD_SCHEMA_VERSION;
}

function parseJson(value: unknown): unknown {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value) as unknown;
  } catch {
    return null;
  }
}

function isUuid(value: unknown): value is string {
  return typeof value === "string" && UUID_RE.test(value);
}

export function isSubAgentSpawnToolName(name: string): boolean {
  return (SUB_AGENT_SPAWN_TOOL_NAMES as readonly string[]).includes(name);
}

/**
 * Recognize only an in-flight spawn with the minimum expected arguments.
 *
 * Arguments are presentation-only until the terminal V1 payload supplies a
 * validated child-session identity. In particular, this helper never derives
 * a route or fabricates a clickable reference from model-controlled input.
 */
export function pendingSubAgentSpawnFromToolCall(
  name: string,
  status: "start" | "end" | "error",
  parameters: unknown,
): PendingSubAgentSpawn | null {
  if (status !== "start" || !isSubAgentSpawnToolName(name)) return null;
  const value = parseJson(parameters);
  if (!isRecord(value)) return null;

  const promptSummary = summarizeSubAgentPrompt(value);
  if (!promptSummary) return null;

  const rawAgentName = value.agent_name;
  const agentId = rawAgentName === undefined || rawAgentName === null
    ? "self"
    : typeof rawAgentName === "string"
      ? rawAgentName.trim()
      : "";
  if (!agentId || agentId.length > 256) return null;

  return { agentId, promptSummary };
}

/**
 * Parse only the exact V1 browser contract carried in a completed spawn result.
 *
 * Both session aliases are required and must agree. `child_session_id` is the
 * canonical V1 field; `session_id` keeps the shape compatible with the plan's
 * original example. No route is derived from prose or from any unknown field.
 */
export function subAgentThreadReferenceFromResult(
  result: unknown,
  currentSessionId: string,
  parameters?: unknown,
): SubAgentThreadReference | null {
  const wrapper = parseJson(result);
  if (!isRecord(wrapper) || !isRecord(wrapper.details)) return null;
  const value = wrapper.details.l9_sub_agent_thread;
  if (!isRecord(value)) return null;

  if (
    value.schema_version !== SUB_AGENT_THREAD_SCHEMA_VERSION
    || value.version !== 1
    || !isUuid(value.task_id)
    || !isUuid(value.child_session_id)
    || !isUuid(value.session_id)
    || value.child_session_id !== value.session_id
    || !isUuid(value.parent_session_id)
    || value.parent_session_id !== currentSessionId
    || value.child_session_id === value.parent_session_id
    || typeof value.agent_id !== "string"
    || value.agent_id.trim().length === 0
    || value.agent_id.length > 256
    || !MODES.has(value.mode as SubAgentThreadMode)
    || !STATES.has(value.state as SubAgentThreadState)
    || typeof value.view_available !== "boolean"
    || !(
      value.parent_run_id === null
      || (typeof value.parent_run_id === "string"
        && value.parent_run_id.length > 0
        && value.parent_run_id.length <= 512)
    )
  ) {
    return null;
  }

  const reason = value.reason_code;
  if (
    (value.view_available === false && !REASONS.has(reason as SubAgentViewUnavailableReason))
    || (value.view_available === true
      && reason !== undefined
      && reason !== null
      && reason !== "session_conflict")
  ) {
    return null;
  }

  return {
    taskId: value.task_id,
    childSessionId: value.child_session_id,
    parentSessionId: value.parent_session_id,
    parentRunId: value.parent_run_id,
    agentId: value.agent_id.trim(),
    mode: value.mode as SubAgentThreadMode,
    state: value.state as SubAgentThreadState,
    viewAvailable: value.view_available,
    reasonCode: reason === undefined || reason === null
      ? null
      : reason as SubAgentViewUnavailableReason,
    promptSummary: summarizeSubAgentPrompt(parameters),
  };
}

/** Build the card preview exclusively from the already-authorized tool args. */
export function summarizeSubAgentPrompt(
  parameters: unknown,
  maxCharacters = 160,
): string | null {
  const value = parseJson(parameters);
  if (!isRecord(value) || typeof value.prompt !== "string") return null;
  const prompt = value.prompt.replace(/\s+/g, " ").trim();
  if (!prompt) return null;
  const characters = Array.from(prompt);
  if (characters.length <= maxCharacters) return prompt;
  return `${characters.slice(0, Math.max(1, maxCharacters - 1)).join("")}…`;
}

export function isTerminalSubAgentState(state: SubAgentThreadState): boolean {
  return ["completed", "failed", "cancelled", "timed_out"].includes(state);
}

export function subAgentStateFromRunStatus(
  status: unknown,
): SubAgentThreadState | null {
  if (typeof status !== "string") return null;
  const normalized = status.toLowerCase();
  if (["completed", "success", "succeeded"].includes(normalized)) return "completed";
  if (["failed", "error"].includes(normalized)) return "failed";
  if (["cancelled", "canceled"].includes(normalized)) return "cancelled";
  if (["timed_out", "timeout"].includes(normalized)) return "timed_out";
  if (["running", "in_progress"].includes(normalized)) return "working";
  if (["pending", "queued"].includes(normalized)) return "queued";
  if (normalized === "paused") return "paused";
  return null;
}
